import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { requiredIf } from '../../../utility/prop-types-helper'
import classnames from 'classnames'
import { capitalize } from 'lodash'
import { useTranslation } from 'react-i18next'
import SearchInput from '../SearchInput'
import InfiniteScroll from '../../infinite-scroll/InfiniteScroll'
import ItemCard from './items/ItemCard'
import Loader from '../loader/legacy-loaders/Op36Loader-web'
import Switcher from '../Switcher'
import { DUAL_PANEL_SELECTABLE_TYPES } from '../../../constants/dual-panel-selector-constants'
import MCElement from '../MCElement'
import classes from './DualPanelSelector.module.scss'
import classes2 from './items/ItemCard.module.scss'
import { debounce } from '../../../utility/debounceHelper'

const SEARCH_PLACEHOLDER = {
  [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages]: 'common.dualPanelSelector.searchPlaceholder.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.programs]: 'common.dualPanelSelector.searchPlaceholder.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.students]: 'common.dualPanelSelector.searchPlaceholder.students',
  [DUAL_PANEL_SELECTABLE_TYPES.series]: 'common.dualPanelSelector.searchPlaceholder.series',
  [DUAL_PANEL_SELECTABLE_TYPES.coaches]: 'common.dualPanelSelector.searchPlaceholder.coaches',
}

const SEARCH_PLACEHOLDER_COMPLETED = {
  [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages]: 'common.dualPanelSelector.searchPlaceholderCompleted.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.programs]: 'common.dualPanelSelector.searchPlaceholderCompleted.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.series]: 'common.dualPanelSelector.searchPlaceholderCompleted.series',
}

const LIST_PLACEHOLDER_TITLE = {
  [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages]: 'common.dualPanelSelector.listTitle.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.programs]: 'common.dualPanelSelector.listTitle.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.students]: 'common.dualPanelSelector.listTitle.students',
  [DUAL_PANEL_SELECTABLE_TYPES.series]: 'common.dualPanelSelector.listTitle.series',
  [DUAL_PANEL_SELECTABLE_TYPES.coaches]: 'common.dualPanelSelector.listTitle.coaches',
}

const LIST_LOADER_TEXT = {
  [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages]: 'common.dualPanelSelector.loaderText.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.programs]: 'common.dualPanelSelector.loaderText.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.students]: 'common.dualPanelSelector.loaderText.students',
  [DUAL_PANEL_SELECTABLE_TYPES.series]: 'common.dualPanelSelector.loaderText.series',
  [DUAL_PANEL_SELECTABLE_TYPES.coaches]: 'common.dualPanelSelector.loaderText.coaches',
}

const Selector = ({
  className,
  type,
  titleText,
  allItems,
  withCompletedSwitcher,
  searchValue,
  onSearchValueChange,
  getSelectedItem,
  fetchItems,
  pagination,
  onCheckChange,
  onSelectChange,
  onSelectDelete,
  itemCategoryTitle,
  itemCategoryCompare,
  handleSelectAllItems,
  allItemsChecked,
  isLoading,
}) => {
  const { t } = useTranslation()
  const [completed, setCompleted] = useState(false)
  const withAdditionalSelect = type === DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages

  const fetchItemsInitial = useCallback((searchTerm, completed = false) => {
    fetchItems(1, searchTerm, completed)
  }, [fetchItems])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(searchValue => {
      fetchItemsInitial(searchValue, completed)
    }),
    [fetchItemsInitial, completed])

  const handleSearchChange = (e) => {
    const value = e.target.value
    onSearchValueChange(value)
    debouncedSearch(value)
  }

  const handleCompletedStateSwitch = () => {
    const newValue = !completed
    setCompleted(newValue)
    fetchItemsInitial(searchValue, newValue)
  }

  useEffect(() => {
    if ((!allItems || allItems.length === 0) && pagination.page === 0) {
      fetchItemsInitial('')
    }
  }, [allItems, fetchItemsInitial, pagination.page])

  const searchPlaceholderKey = withCompletedSwitcher && completed ? SEARCH_PLACEHOLDER_COMPLETED[type] : SEARCH_PLACEHOLDER[type]
  const listTitleKey = LIST_PLACEHOLDER_TITLE[type]
  const loaderTextKey = LIST_LOADER_TEXT[type]

  return (
    <div className={classnames('d-flex flex-column', classes.selector, className)}>
      {!!titleText && <h3 className={classes.title}>{titleText}</h3>}
      {withCompletedSwitcher && (
        <Switcher
          isSwitched={completed}
          onClick={handleCompletedStateSwitch}
          options={[t('common.dualPanelSelector.active'), t('common.dualPanelSelector.completed')]}
          className='mb-4 flex-shrink-0 w-100'
        />
      )}
      {searchValue !== undefined && (
        <SearchInput
          className={classes.searchBar}
          placeholder={searchPlaceholderKey ? t(searchPlaceholderKey) : undefined}
          value={searchValue}
          onChange={handleSearchChange}
        />
      )}
      <hr className={classes.hr} />

      {handleSelectAllItems && (
        <div
          className={classnames(
            'd-flex align-items-center',
            classes2.itemWrapper
          )}
        >
          <div className={classes2.checkboxWrapper}>
            <MCElement
              type='checkbox'
              isSelected={allItemsChecked}
              clicked={handleSelectAllItems}
            />
          </div>
          <div className={classnames('d-flex ml-5 align-items-center', classes2.selectAllText)}>
            {t('common.dualPanelSelector.selectAll', { type: capitalize(type) })}
          </div>
        </div>
      )}

      {(isLoading && pagination.page === 0) || (isLoading && allItemsChecked) ? (
        <Loader message={loaderTextKey ? t(loaderTextKey) : undefined} />
      ) : allItems?.length > 0 ? (
        <div id='infinite-scroll-wrapper' className={classnames('pb-1', classes.listWrapper)}>
          <InfiniteScroll
            className={classes.list}
            dataLength={allItems.length}
            next={() => fetchItems(pagination.page + 1, searchValue, completed)}
            hasMore={!pagination.isLastPage}
            scrollableTarget='infinite-scroll-wrapper'
          >
            {allItems.map((item, index, items) => {
              const selectedItem = getSelectedItem(item.id)
              const displayedItem = selectedItem ?? item

              return (
                <ItemCard
                  key={displayedItem.id}
                  type={type}
                  item={displayedItem}
                  hasNewCategory={!completed && !!itemCategoryCompare && (index === 0 || itemCategoryCompare(displayedItem, items[index - 1]) > 0)}
                  categoryTitle={!completed && !!itemCategoryTitle && itemCategoryTitle(displayedItem)}
                  isSelectable
                  isChecked={!!selectedItem}
                  withAdditionalSelect={withAdditionalSelect}
                  onCheckChange={onCheckChange}
                  onSelect={onSelectChange}
                  onSelectDelete={onSelectDelete}
                />
              )
            })}
          </InfiniteScroll>
        </div>
      ) : (
        <div className={classnames('d-flex flex-column justify-content-center align-items-center', classes.placeholder)}>
          <h4 className={classes.title}>
            {searchValue
              ? t('common.dualPanelSelector.noResultsFor', { searchValue })
              : (listTitleKey ? t(listTitleKey) : undefined)}
          </h4>
          <p className={classes.text}>{searchValue ? t('common.dualPanelSelector.checkSpelling') : t('common.dualPanelSelector.pleaseTryAgain')}</p>
        </div>
      )}
    </div>
  )
}

export const TabProps = {
  titleText: PropTypes.string,
  allItems: PropTypes.array,
  withCompletedSwitcher: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchValueChange: requiredIf(PropTypes.func, ({ searchValue }) => searchValue !== undefined),
  getSelectedItem: PropTypes.func.isRequired,
  fetchItems: PropTypes.func.isRequired,
  pagination: PropTypes.object.isRequired,
  onCheckChange: PropTypes.func.isRequired,
  onSelectChange: requiredIf(PropTypes.func, ({ type }) => type === DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages),
  onSelectDelete: requiredIf(PropTypes.func, ({ type }) => type === DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages),
  itemCategoryTitle: PropTypes.func,
  itemCategoryCompare: requiredIf(PropTypes.func, ({ itemCategoryKey }) => !!itemCategoryKey),
  handleSelectAllItems: PropTypes.func,
  allItemsChecked: PropTypes.bool,
  isLoading: PropTypes.bool,
}

Selector.propTypes = {
  ...TabProps,
  type: PropTypes.oneOf(Object.values(DUAL_PANEL_SELECTABLE_TYPES)).isRequired,
}

export default Selector
